import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { logger } from '../utils/logger';
import { createPersistMiddleware, PERSIST_VERSION, persistMigrationManager } from './persistMigration';

export interface PersistConfigState {
  // Versión actual del esquema de persist
  persistVersion: string;
  
  // Información de la última migración
  lastMigration: {
    fromVersion: string;
    toVersion: string;
    timestamp: string;
    success: boolean;
  } | null;
  
  // Estado de migración actual
  isMigrating: boolean;
  migrationProgress: number;
  migrationMessage: string;
  
  // Configuración de persist
  persistEnabled: boolean;
  autoMigrate: boolean;
  
  // Acciones
  setPersistVersion: (version: string) => void;
  setLastMigration: (migration: PersistConfigState['lastMigration']) => void;
  setMigrating: (isMigrating: boolean, progress?: number, message?: string) => void;
  setPersistEnabled: (enabled: boolean) => void;
  setAutoMigrate: (enabled: boolean) => void;
  
  // Acciones de migración
  startMigration: (fromVersion: string, toVersion: string) => void;
  completeMigration: (success: boolean) => void;
  clearMigrationState: () => void;
  
  // Acciones de limpieza
  clearAllPersistedData: () => void;
  resetToDefaults: () => void;
}

const initialState = {
  persistVersion: PERSIST_VERSION,
  lastMigration: null,
  isMigrating: false,
  migrationProgress: 0,
  migrationMessage: '',
  persistEnabled: true,
  autoMigrate: true,
};

export const usePersistConfigStore = create<PersistConfigState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        setPersistVersion: (version: string) => {
          logger.log('PERSIST_CONFIG', `🔄 Actualizando versión de persist: ${version}`);
          set({ persistVersion: version });
        },
        
        setLastMigration: (migration) => {
          set({ lastMigration: migration });
          if (migration) {
            logger.log('PERSIST_CONFIG', `✅ Migración registrada: v${migration.fromVersion} → v${migration.toVersion}`);
          }
        },
        
        setMigrating: (isMigrating, progress = 0, message = '') => {
          set({ 
            isMigrating, 
            migrationProgress: progress, 
            migrationMessage: message 
          });
          
          if (isMigrating) {
            logger.log('PERSIST_CONFIG', `🔄 Iniciando migración: ${message} (${progress}%)`);
          } else {
            logger.log('PERSIST_CONFIG', '✅ Migración completada');
          }
        },
        
        setPersistEnabled: (enabled) => {
          set({ persistEnabled: enabled });
          logger.log('PERSIST_CONFIG', `🔧 Persist ${enabled ? 'habilitado' : 'deshabilitado'}`);
        },
        
        setAutoMigrate: (enabled) => {
          set({ autoMigrate: enabled });
          logger.log('PERSIST_CONFIG', `🔧 Auto-migración ${enabled ? 'habilitada' : 'deshabilitada'}`);
        },
        
        startMigration: (fromVersion: string, toVersion: string) => {
          set({
            isMigrating: true,
            migrationProgress: 0,
            migrationMessage: `Migrando de v${fromVersion} a v${toVersion}`,
            lastMigration: {
              fromVersion,
              toVersion,
              timestamp: new Date().toISOString(),
              success: false
            }
          });
          
          logger.log('PERSIST_CONFIG', `🚀 Iniciando migración: v${fromVersion} → v${toVersion}`);
        },
        
        completeMigration: (success: boolean) => {
          const current = get();
          set({
            isMigrating: false,
            migrationProgress: 100,
            migrationMessage: success ? 'Migración completada exitosamente' : 'Error en migración',
            lastMigration: current.lastMigration ? {
              ...current.lastMigration,
              success
            } : null
          });
          
          if (success) {
            logger.log('PERSIST_CONFIG', '🎉 Migración completada exitosamente');
          } else {
            logger.error('PERSIST_CONFIG', '❌ Migración falló');
          }
        },
        
        clearMigrationState: () => {
          set({
            isMigrating: false,
            migrationProgress: 0,
            migrationMessage: '',
            lastMigration: null
          });
          
          logger.log('PERSIST_CONFIG', '🧹 Estado de migración limpiado');
        },
        
        clearAllPersistedData: () => {
          try {
            persistMigrationManager.clearPersistedState();
            set(initialState);
            logger.log('PERSIST_CONFIG', '🧹 Todos los datos persistidos limpiados');
          } catch (error) {
            logger.error('PERSIST_CONFIG', '❌ Error al limpiar datos persistidos:', error);
          }
        },
        
        resetToDefaults: () => {
          set(initialState);
          logger.log('PERSIST_CONFIG', '🔄 Configuración de persist reseteada a valores por defecto');
        },
      }),
      createPersistMiddleware('persist-config-store', { version: PERSIST_VERSION })
    ),
    {
      name: 'persist-config-store',
    }
  )
);
